/**
 * Reconnect policy for the browser↔helper key-grant socket
 * (`/api/keygrant/ws`, `KeyGrantRelay` on the helper).
 *
 * The helper relays module requests to ONE browser page. Two Catalog.ID pages
 * in the desktop at once (the dashboard tab plus a window the storefront or a
 * print opened, say) used to abort each other every second, forever: the relay
 * replaced the previous tab on every new connection and the replaced client
 * reconnected after one second (found live 2026-09-14 — CPR's "Get a token"
 * timed out with nothing shown, because no page held the socket long enough
 * to receive the request).
 *
 * The rule now: the FIRST connected page keeps the slot. A later page is
 * refused with close code {@link CLOSE_SLOT_TAKEN} and waits — on a slow
 * schedule, and immediately when it is focused or becomes visible — for the
 * slot to free (the first page closed or reloaded). A refused attempt costs one
 * handshake and disturbs nobody.
 */

/** Close code the relay sends when another page already holds the slot. */
export const CLOSE_SLOT_TAKEN = 4001;

/** After a dropped socket (helper restarted, network): exponential from 1 s. */
export const RECONNECT_INITIAL_MS = 1000;
export const RECONNECT_MAX_MS = 30_000;

/** After a refusal: never within the first second, never a storm. */
export const SLOT_TAKEN_INITIAL_MS = 2000;
export const SLOT_TAKEN_MAX_MS = 15_000;

export type ReconnectReason = 'dropped' | 'slot-taken';

export interface ReconnectPlan {
    reason: ReconnectReason;
    /** Wait this long before the next attempt. */
    delayMs: number;
    /** What to carry as the current delay into the next plan. */
    nextDelayMs: number;
}

export function planReconnect(closeCode: number | undefined, currentDelayMs: number): ReconnectPlan {
    if (closeCode === CLOSE_SLOT_TAKEN) {
        // Clamp into the slow schedule: a 30 s drop backoff would miss the slot
        // freeing for too long, a 1 s one would knock every second.
        const delayMs = Math.min(Math.max(currentDelayMs, SLOT_TAKEN_INITIAL_MS), SLOT_TAKEN_MAX_MS);
        return { reason: 'slot-taken', delayMs, nextDelayMs: Math.min(delayMs * 2, SLOT_TAKEN_MAX_MS) };
    }
    const delayMs = Math.min(currentDelayMs, RECONNECT_MAX_MS);
    return { reason: 'dropped', delayMs, nextDelayMs: Math.min(delayMs * 2, RECONNECT_MAX_MS) };
}
